import { useCallback, useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import AppContext from '@/context/AppContext'
import routes from '@/Router/routes.constants'
import services from '@/services/services'

const useVerifyEmail = ({ sendValidationCodeOnInit, setToastInfo }) => {
  const { t } = useTranslation()
  const navigation = useNavigate()
  const { context, setContext } = useContext(AppContext)
  const isValidationCodeSentOnInit = useRef(false)
  const { fetchData: requestToResendValidationCode } = useFetch(services.resendVerifyEmail, { doInitialRequest: false })
  const { fetchData: requestToSendValidationCode } = useFetch(services.sendValidationCode, { doInitialRequest: false })
  const email = {
    email: context?.orderInfo?.customer_profile?.email?.email,
    id: context?.orderInfo?.customer_profile?.email?.id,
  }

  const resendValidationCode = useCallback(async () => {
    await requestToResendValidationCode({ uuid: email.id, email: email.email })
  }, [email.email, email.id, requestToResendValidationCode])

  useEffect(() => {
    if (sendValidationCodeOnInit && !isValidationCodeSentOnInit.current) {
      isValidationCodeSentOnInit.current = true
      resendValidationCode()
    }
  }, [requestToResendValidationCode, resendValidationCode, sendValidationCodeOnInit])

  const sendValidationCode = async ({ code, paymentMethodId }) => {
    const data = await requestToSendValidationCode({ uuid: email.id, code, paymentMethodId })

    setContext(context => ({
      ...context,
      orderInfo: {
        ...context.orderInfo,
        customer_profile: {
          ...context.orderInfo.customer_profile,
          email: { ...context.orderInfo.customer_profile.email, verified: true },
        },
      },
    }))

    return data?.verification_code_id
  }

  const validateEmail = async code => {
    try {
      await sendValidationCode({
        code,
        paymentMethodId: context?.orderInfo?.bankAccount?.id,
      })

      navigation(routes.summaryPage, { replace: true })
    } catch {
      setToastInfo({ show: true, message: t('Error al verificar el email'), type: 'error' })
    }
  }

  const onResendEmail = async () => {
    try {
      await resendValidationCode()
      setToastInfo({ show: true, message: t('Código de verificación enviado'), type: 'success' })
    } catch {
      setToastInfo({ show: true, message: t('Error al enviar el código de verificación'), type: 'error' })
    }
  }

  return { email, resendValidationCode, sendValidationCode, validateEmail, onResendEmail }
}

export default useVerifyEmail
